import {createGamingTelemetry,type GamingTelemetrySink} from './telemetry';
import type { GamingControlProvider, GamingSession } from '../net/api';
export type { GamingSession } from '../net/api';
export interface GamingState { session: GamingSession | null; busy: boolean; mutating: boolean; error: string | null; blocked: boolean }
type Hint = { streamId: string; sessionId: string };
const HINT_KEY = 'perck.gaming.session';
/** Control-plane only: has no access to program tracks, encoders or broadcast lifecycle. */
export class GamingController {
  runtimeMedia?:Pick<import('./runtime-media').RuntimeMediaProducer,'sync'|'waitReady'|'dispose'>;
  program?:import('./creator-program').CreatorProgram;
  creator?: import('./creator-source-owner').CreatorSourceOwner;
  reaction?: import('./reaction-controller').ReactionController;
  private state: GamingState = { session: null, busy: false, mutating: false, error: null, blocked: false };
  private listeners = new Set<() => void>();
  private pending: { key: string; promise: Promise<GamingSession>; abort: AbortController } | null = null;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private generation = 0;
  private disposed = false;
  private liveId: string | null = null;
  private hint: Hint | null = null;
  private unsubscribeSession?:()=>void;
  private lifecycleHint?:{sessionId:string;streamId:string;epoch:number;revision:number};
  readonly enabled: boolean;
  constructor(private readonly options: { telemetry?:GamingTelemetrySink; enabled: boolean; provider?: GamingControlProvider; liveStreamId: () => string | null; storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> }) {
    this.enabled = options.enabled && !!options.provider;
    if(this.enabled)this.unsubscribeSession=options.provider?.subscribeSession?.(hint=>{
      const session=this.state.session;
      if(this.disposed||hint.streamId!==options.liveStreamId()||session&&(hint.sessionId!==session.sessionId||hint.epoch!==session.epoch||hint.revision<=session.revision))return;
      if(!this.lifecycleHint||hint.revision>this.lifecycleHint.revision)this.lifecycleHint=hint;
      this.reconcileLifecycle();
    });
    if (this.enabled) try {
      const hint = JSON.parse(options.storage?.getItem(HINT_KEY) ?? 'null');
      if (hint && typeof hint.streamId === 'string' && typeof hint.sessionId === 'string' && hint.streamId.length > 0 && hint.sessionId.length > 0 && hint.streamId.length <= 128 && hint.sessionId.length <= 128) { this.hint = hint; this.state = { ...this.state, blocked: true }; }
    } catch { /* Storage is optional; never restores authority. */ }
  }
  getState = (): GamingState => this.state;
  subscribe = (listener: () => void): (() => void) => { this.listeners.add(listener); return () => this.listeners.delete(listener); };
  private update(patch: Partial<GamingState>): void { this.state = { ...this.state, ...patch }; this.listeners.forEach(fn => fn()); this.reaction?.sync(); this.creator?.sync();this.program?.sync(); }
  private fail(message: string): Promise<GamingSession> { this.update({ error: message }); return Promise.reject(new Error(message)); }
  private remember(hint: Hint | null): void {
    this.hint = hint;
    try { if (hint) this.options.storage?.setItem(HINT_KEY, JSON.stringify(hint)); else this.options.storage?.removeItem(HINT_KEY); } catch { /* Optional nonsecret recovery hint. */ }
  }
  private observeStream(streamId: string | null): void {
    if (this.liveId === streamId) return;
    this.liveId = streamId; this.lifecycleHint=undefined; this.generation++; clearTimeout(this.timer); this.pending?.abort.abort(); this.pending = null;
    this.update({ session: null, busy: false, mutating: false, blocked: false, error: null });
    if (streamId && this.hint?.streamId !== streamId) this.remember(null);
  }
  /** Called on broadcast state changes; a hint is only accepted after canonical owner-fenced read. */
  syncBroadcast(): void {
    if (!this.enabled || this.disposed) return;
    const streamId = this.options.liveStreamId(), changed = streamId !== this.liveId;
    this.observeStream(streamId);
    if (changed && streamId && this.hint) void this.refresh().catch(() => undefined);
  }
  private reconcileLifecycle():boolean {
    const hint=this.lifecycleHint,session=this.state.session;
    if(!hint||this.pending||this.disposed||!session)return false;
    this.lifecycleHint=undefined;
    if(hint.sessionId!==session.sessionId||hint.streamId!==this.options.liveStreamId()||hint.epoch!==session.epoch||hint.revision<=session.revision)return false;
    void this.refresh().catch(()=>undefined);return true;
  }
  private schedule(): void {
    clearTimeout(this.timer);
    if (!this.disposed && this.state.session && this.state.session.state !== 'ENDED') {
      // One timer/read at a time, <=30 reads/min; stop on errors until explicit Refresh.
      this.timer = setTimeout(() => { void this.refresh().catch(() => undefined); }, 2_000);
    }
  }
  prepare(versionId: string): Promise<GamingSession> { return this.run('prepareSession', versionId); }
  activate(sessionId: string): Promise<GamingSession> { return this.run('activateSession',sessionId); }
  end(sessionId: string): Promise<GamingSession> { void this.program?.returnToConventional('owner_end').catch(()=>undefined);this.creator?.stop(); return this.run('endSession', sessionId); }
  refresh(): Promise<GamingSession> {
    const id = this.state.session?.sessionId ?? this.hint?.sessionId;
    return id ? this.run('getSession', id) : this.fail('No known Gaming session to recover.');
  }
  dispose(): void { this.program?.dispose();this.creator?.dispose(); this.unsubscribeSession?.();this.lifecycleHint=undefined; void this.reaction?.stop(); this.disposed = true; this.generation++; clearTimeout(this.timer); this.pending?.abort.abort(); this.pending = null; this.update({ session: null, blocked: true, busy: false, mutating: false }); this.listeners.clear(); }
  private run(operation: 'prepareSession' | 'activateSession' | 'endSession' | 'getSession', id: string): Promise<GamingSession> {
    if (this.disposed) return Promise.reject(new Error('Gaming controls disposed.'));
    const streamId = this.options.liveStreamId(); this.observeStream(streamId);
    const key = `${operation}:${id}`;
    if (this.pending?.key === key) return this.pending.promise;
    if (this.pending && operation !== 'endSession') return Promise.reject(new Error('Gaming action in progress.'));
    if (!this.enabled || !this.options.provider || !streamId) return this.fail('Gaming requires an authenticated portal and a live stream.');
    if (!id.trim() || id.length > 128) return this.fail('Invalid Gaming identifier.');
    const known = this.state.session ?? this.hint;
    if (operation !== 'prepareSession' && (known?.sessionId !== id || known.streamId !== streamId)) return this.fail('Gaming session does not belong to the current broadcast.');
    if (operation === 'prepareSession' && (this.state.blocked || this.hint && this.state.session?.state !== 'ENDED')) return this.fail('Refresh or end the current Gaming session first.');
    if (this.pending) { this.generation++; this.pending.abort.abort(); this.pending = null; }
    const abort = new AbortController();
    const program=operation==='activateSession'?this.program:undefined,pinned=this.state.session;
    clearTimeout(this.timer); this.update({ busy: true, mutating: operation !== 'getSession'&&!program, error: null });
    const generation = this.generation;
    const current = () => !this.disposed && !abort.signal.aborted && generation === this.generation && this.options.liveStreamId() === streamId;
    let accepted = false,submitted=false,uncertain=false;
    const request=()=>{submitted=true;return this.options.provider!.request(operation,id,streamId,undefined,{signal:abort.signal,current});};
    const bounded=<T>(work:Promise<T>)=>new Promise<T>((resolve,reject)=>{
      const stop=()=>reject(Error('Gaming activation cancelled.'));
      if(abort.signal.aborted){void work.catch(()=>undefined);stop();return;}
      abort.signal.addEventListener('abort',stop,{once:true});
      work.then(resolve,reject).finally(()=>abort.signal.removeEventListener('abort',stop));
    });
    const work=program?Promise.resolve().then(async()=>{
      if(!current()||!pinned||pinned.sessionId!==id)throw Error('Gaming activation cancelled.');
      await bounded(program.activateIntent(pinned,abort.signal));
      const producer=program.mountedProducer();
      if(!producer||!this.runtimeMedia)throw Error('creator_program_unavailable');
      await bounded(this.runtimeMedia.waitReady(pinned,abort.signal));
      const session=this.state.session;
      if(!current()||program!==this.program||program.mountedProducer()!==producer||!session||session.sessionId!==pinned.sessionId||session.revision!==pinned.revision||session.epoch!==pinned.epoch||session.transitionId!==pinned.transitionId||session.closeRequest)throw Error('Gaming activation cancelled.');
      this.update({mutating:true});
      if(!current()||program.mountedProducer()!==producer)throw Error('Gaming activation cancelled.');
      return request();
    }):request();
    const promise = work.then(session => {
      if (!current() || session.streamId !== streamId || (operation === 'prepareSession' ? session.versionId !== id : session.sessionId !== id)) throw new Error('Broadcast changed.');
      if (operation === 'getSession' && this.state.session && session.revision < this.state.session.revision) throw new Error('Stale Gaming session.');
      if(operation==='prepareSession')createGamingTelemetry(this.options.telemetry)('prepare',session);
      this.remember({ sessionId: session.sessionId, streamId });
      this.update({ session, blocked: false }); accepted = true; return session;
    }).catch(async(error: unknown) => {
      if(program&&current()){uncertain=submitted;void program.returnToConventional('activation_failed').catch(()=>undefined);}
      if (current()) {
        const busy = error instanceof Error && 'code' in error && error.code === 'GAMING_BUSY';
        if (operation === 'getSession' && busy) {
          // Expected bridge contention is not evidence that session authority was revoked.
          accepted = true;
        } else {
          this.update({ ...(operation === 'getSession' ? { session: null, blocked: true } : {}), error: error instanceof Error ? error.message : 'Gaming unavailable.' });
        }
      }
      else if (!this.disposed) this.observeStream(this.options.liveStreamId());
      throw error;
    }).finally(async() => {
      if (current()) {
        this.pending=null;abort.abort();this.update({busy:false,mutating:false});
        if(uncertain){
          // Submitted activation may have committed despite a lost reply. Retired output never re-enters.
          try {const canonical=await this.refresh();if(!this.disposed&&generation===this.generation&&this.options.liveStreamId()===streamId&&canonical.sessionId===id&&!canonical.closeRequest&&['PREPARING','ACTIVE'].includes(canonical.state))await this.end(id);}catch{/* Canonical failure keeps recovery blocked; no local readiness is restored. */}
        }else if(accepted&&!this.reconcileLifecycle())this.schedule();
      }
    });
    this.pending = { key, promise, abort }; return promise;
  }
}
